// Szkielet gramatyczny kursu (SPEC §21.3–21.4): lista pozycji uczonych etapami i ich wykrywacze w zdaniach lekcji.
// To lista SKURATOROWANA, a nie przepisana z CEFR-J Grammar Profile (ten plik jest szumny — np. w A1.1 są „subordinate clause”
// i „preposition stranding”); w polu `cefrj` zapisano numery pozycji CEFR-J, które dana pozycja obejmuje. Wykrywacze są
// zachowawcze: lekcja musi pokazać swój temat gramatyczny (błąd, gdy go nie ma), a zbyt zaawansowaną gramatykę zgłaszamy
// tylko jako ostrzeżenie (możliwy fałszywy alarm).

import { irregularVerbLemmas } from './lemma.ts';
import type { Stage } from './levels.ts';
import { stageRank } from './levels.ts';
import type { Match } from './cefr.ts';

export interface AnalyzedToken {
  raw: string;
  lower: string;
  sentenceStart: boolean;
  matches: Match[];
}

export interface AnalyzedSentence {
  text: string;
  /** Tekst małymi literami z apostrofami prostymi — do wyrażeń regularnych. */
  lower: string;
  tokens: AnalyzedToken[];
}

export interface GrammarItem {
  id: string;
  label: string;
  labelPl: string;
  stage: Stage;
  /** Numery pozycji CEFR-J Grammar Profile (kolumna ID), które ta pozycja obejmuje. */
  cefrj: string[];
  detect: (sentences: readonly AnalyzedSentence[]) => boolean;
}

const verbMatch = (t: AnalyzedToken): boolean => t.matches.some((m) => ['verb', 'have', 'do', 'be', 'modal'].includes(m.entry.pos));
const BE = new Set(['am', 'is', 'are', 'was', 'were', 'be', 'been', 'being']);
const SUBJECT_BASE = new Set(['i', 'you', 'we', 'they']);

/** Forma nieregularnego czasownika, która nie jest ani hasłem, ani -s, ani -ing (np. went, ate, seen, taken). */
const PRESENT_FORMS = new Set(['am', 'is', 'are']);
function irregularPastOrParticiple(t: AnalyzedToken): boolean {
  if (PRESENT_FORMS.has(t.lower)) return false;
  return irregularVerbLemmas(t.lower).some((lemma) => lemma !== t.lower && !t.lower.endsWith('s') && !t.lower.endsWith('ing'));
}
function isParticiple(t: AnalyzedToken | undefined): boolean {
  if (!t) return false;
  if (irregularPastOrParticiple(t)) return true;
  return t.lower.endsWith('ed') && t.matches.some((m) => m.kind === 'past' && m.entry.pos === 'verb');
}
function isIng(t: AnalyzedToken): boolean {
  return t.lower.endsWith('ing') && t.matches.some((m) => m.lemma !== t.lower && ['verb', 'have', 'do', 'be'].includes(m.entry.pos));
}
const DETERMINERS = new Set(['the', 'a', 'an', 'my', 'your', 'his', 'her', 'its', 'our', 'their', 'some', 'these', 'those', 'many', 'two', 'three', 'four', 'five', 'all', 'no']);
/** Czasownik w 3. osobie liczby pojedynczej (eats, has, goes) — nie rzeczownik w liczbie mnogiej po przedimku („the books”). */
function isThirdPersonVerb(t: AnalyzedToken, prev?: AnalyzedToken): boolean {
  if (['has', 'does', 'goes', 'says'].includes(t.lower)) return true;
  if (t.sentenceStart || !t.lower.endsWith('s') || ['is', 'was', 'this', 'his', 'its', 'us', 'yes'].includes(t.lower)) return false;
  if (prev && DETERMINERS.has(prev.lower)) return false;
  return t.matches.some((m) => m.lemma !== t.lower && ['verb', 'have', 'do'].includes(m.entry.pos));
}

const anyText = (ss: readonly AnalyzedSentence[], re: RegExp): boolean => ss.some((s) => re.test(s.lower));
const anyTokens = (ss: readonly AnalyzedSentence[], f: (tokens: AnalyzedToken[], i: number) => boolean): boolean =>
  ss.some((s) => s.tokens.some((_, i) => f(s.tokens, i)));

export const GRAMMAR: readonly GrammarItem[] = [
  {
    id: 'this-it-is', label: 'This is / It is', labelPl: 'To jest / Jest (this, it)', stage: 'A1.1', cefrj: ['8', '10', '58'],
    detect: (ss) => anyText(ss, /\b(this|that|it) is\b|\b(this|that|it)'s\b/),
  },
  {
    id: 'be-statements', label: 'I am / he is / they are', labelPl: 'Być: jestem, jest, są', stage: 'A1.1', cefrj: ['1', '3', '4', '5', '58'],
    detect: (ss) => anyText(ss, /\b(am|is|are)\b|'(m|s|re)\b/),
  },
  {
    id: 'present-simple', label: 'Present simple (he eats, they drink)', labelPl: 'Czas teraźniejszy prosty', stage: 'A1.1', cefrj: ['59', '60'],
    detect: (ss) =>
      anyTokens(ss, (t, i) => {
        const tok = t[i]!;
        const prev = t[i - 1];
        if (isThirdPersonVerb(tok, prev)) return true;
        return !!prev && SUBJECT_BASE.has(prev.lower) && verbMatch(tok) && !BE.has(tok.lower) && tok.lower !== 'can';
      }),
  },
  {
    id: 'imperative', label: 'Commands (Look! Come here.)', labelPl: 'Tryb rozkazujący', stage: 'A1.1', cefrj: ['117'],
    detect: (ss) => anyTokens(ss, (t, i) => i === 0 && !!t[i]!.matches.length && t[i]!.matches.some((m) => m.entry.pos === 'verb' && m.kind === 'base')),
  },
  {
    id: 'can-ability', label: 'can / can’t', labelPl: 'Umiem / mogę (can)', stage: 'A1.2', cefrj: ['123', '123-2'],
    detect: (ss) => anyText(ss, /\bcan\b|\bcan't\b|\bcannot\b/),
  },
  {
    id: 'there-is-are', label: 'There is / There are', labelPl: 'Jest / są (there is, there are)', stage: 'A1.2', cefrj: ['146'],
    detect: (ss) => anyText(ss, /\bthere (is|are)\b|\bthere's\b/),
  },
  {
    id: 'want-to', label: 'want / like / need + to', labelPl: 'Chcę, lubię, potrzebuję + to', stage: 'A1.2', cefrj: ['101', '103'],
    detect: (ss) => anyText(ss, /\b(want|wants|like|likes|love|loves|need|needs|hope|hopes)\s+(\w+\s+)?to\b/),
  },
  {
    id: 'frequency', label: 'always / usually / often / never', labelPl: 'Przysłówki częstotliwości', stage: 'A1.2', cefrj: ['32', '35'],
    detect: (ss) => anyText(ss, /\b(always|usually|often|sometimes|never|frequently)\b/),
  },
  {
    id: 'present-continuous', label: 'Present continuous (is eating)', labelPl: 'Czas teraźniejszy ciągły', stage: 'A1.3', cefrj: ['61'],
    detect: (ss) => anyTokens(ss, (t, i) => isIng(t[i]!) && !!t.slice(Math.max(0, i - 2), i).some((p) => ['am', 'is', 'are', "i'm", "he's", "she's", "it's", "we're", "they're", "you're"].includes(p.lower))),
  },
  {
    id: 'past-simple', label: 'Past simple (went, was, played)', labelPl: 'Czas przeszły prosty', stage: 'A1.3', cefrj: ['64', '65'],
    detect: (ss) => anyTokens(ss, (t, i) => ['was', 'were', 'did'].includes(t[i]!.lower) || irregularPastOrParticiple(t[i]!) || (t[i]!.lower.endsWith('ed') && t[i]!.matches.some((m) => m.kind === 'past'))),
  },
  {
    id: 'comparative', label: 'Comparatives (bigger, more…)', labelPl: 'Stopień wyższy', stage: 'A1.3', cefrj: ['38', '39', '40'],
    detect: (ss) =>
      anyText(ss, /\bmore \w+|\bthan\b/) || anyTokens(ss, (t, i) => t[i]!.matches.some((m) => m.kind === 'comparative' || m.kind === 'superlative')),
  },
  {
    id: 'future-will', label: 'will / won’t', labelPl: 'Czas przyszły (will)', stage: 'A2.1', cefrj: ['69', '141'],
    detect: (ss) => anyText(ss, /\bwill\b|'ll\b|\bwon't\b/),
  },
  {
    id: 'present-perfect', label: 'Present perfect (I have seen)', labelPl: 'Present perfect', stage: 'A2.1', cefrj: ['62'],
    detect: (ss) =>
      anyTokens(ss, (t, i) => {
        if (!['have', 'has', "i've", "you've", "we've", "they've", "he's", "she's"].includes(t[i]!.lower)) return false;
        const n1 = t[i + 1];
        const n2 = t[i + 2];
        return isParticiple(n1) || n1?.lower === 'been' || (['not', 'never', 'just', 'already', 'ever'].includes(n1?.lower ?? '') && (isParticiple(n2) || n2?.lower === 'been'));
      }),
  },
  {
    id: 'should-would', label: 'should / would', labelPl: 'Powinienem / chciałbym', stage: 'A2.1', cefrj: ['139', '142'],
    detect: (ss) => anyText(ss, /\b(should|would)\b|'d like\b/),
  },
  {
    id: 'going-to', label: 'be going to (plans)', labelPl: 'Plany: be going to', stage: 'A2.2', cefrj: ['122'],
    detect: (ss) => anyText(ss, /\b(am|is|are|'m|'s|'re) going to\b|\b(i'm|he's|she's|it's|we're|they're|you're) going to\b/),
  },
  {
    id: 'have-to', label: 'have to / need to', labelPl: 'Muszę (have to, need to)', stage: 'A2.2', cefrj: ['127', '136'],
    detect: (ss) => anyText(ss, /\b(have|has|had|need|needs) to\b/),
  },
  {
    id: 'modals-b1', label: 'could / may / might / must', labelPl: 'Czasowniki modalne (could, may, might, must)', stage: 'B1.1', cefrj: ['124', '129', '132', '135'],
    detect: (ss) => anyText(ss, /\b(could|may|might|must)\b/),
  },
  {
    id: 'present-perfect-neg-q', label: 'Present perfect: haven’t / Have you …?', labelPl: 'Present perfect: przeczenia i pytania', stage: 'B1.1', cefrj: ['62-1'],
    detect: (ss) =>
      anyText(ss, /\b(haven't|hasn't)\b|\b(have|has) not\b|\b(have|has) (you|i|we|they|he|she|it)\b[^.?!]*\?/) ||
      anyTokens(ss, (t, i) => t[i]!.sentenceStart && ['have', 'has'].includes(t[i]!.lower) && t.slice(i + 1).some((x) => isParticiple(x))),
  },
  {
    id: 'passive', label: 'Passive voice (is made, was built)', labelPl: 'Strona bierna', stage: 'B2.1', cefrj: ['73', '76', '79', '82'],
    detect: (ss) =>
      anyTokens(ss, (t, i) => {
        if (!BE.has(t[i]!.lower)) return false;
        const n1 = t[i + 1];
        const n2 = t[i + 2];
        return isParticiple(n1) || (['not', 'never', 'always', 'also', 'often', 'now', 'carefully'].includes(n1?.lower ?? '') && isParticiple(n2));
      }),
  },
  {
    id: 'conditional-2', label: 'Second conditional (If I had …, I would …)', labelPl: 'Okres warunkowy II', stage: 'B2.2', cefrj: ['215'],
    detect: (ss) => anyText(ss, /\bif\b[^.?!]*\b(would|could|might)\b|\b(would|could)\b[^.?!]*\bif\b/),
  },
];

const BY_ID = new Map(GRAMMAR.map((g) => [g.id, g]));
export const grammarById = (id: string): GrammarItem | undefined => BY_ID.get(id);

/** Pozycje z etapów WYŻSZYCH niż `stage`, których wykrywacz rozpoznaje coś w zdaniach (możliwy fałszywy alarm → ostrzeżenie). */
export function higherStageGrammar(stage: Stage, sentences: readonly AnalyzedSentence[]): GrammarItem[] {
  return GRAMMAR.filter((g) => stageRank(g.stage) > stageRank(stage) && g.detect(sentences));
}
